import { get, post, put, del } from './api.service';
import {
  Employee,
  EmployeeCreateInput,
  EmployeeUpdateInput,
  Attendance,
  AttendanceCreateInput,
  AttendanceBulkInput,
  AttendanceFilters,
  AttendanceSummary,
  AttendanceStatus,
  SalarySlip,
  SalarySlipCreateInput,
  SalarySlipFilters,
  SalarySlipStatus,
  PayrollDashboard,
} from '../types';

// ============================================================================
// EMPLOYEE API
// ============================================================================

export async function getEmployees(activeOnly = false): Promise<{ employees: Employee[] }> {
  return get<{ employees: Employee[] }>(`/salary/employees?activeOnly=${activeOnly}`);
}

export async function getEmployeeById(employeeId: string): Promise<{ employee: Employee }> {
  return get<{ employee: Employee }>(`/salary/employees/${employeeId}`);
}

export async function createEmployee(data: EmployeeCreateInput): Promise<{ employee: Employee }> {
  return post<{ employee: Employee }>('/salary/employees', data);
}

export async function updateEmployee(
  employeeId: string,
  data: EmployeeUpdateInput
): Promise<{ employee: Employee }> {
  return put<{ employee: Employee }>(`/salary/employees/${employeeId}`, data);
}

export async function deleteEmployee(employeeId: string, permanent = false): Promise<{ message: string }> {
  return del<{ message: string }>(`/salary/employees/${employeeId}${permanent ? '?permanent=true' : ''}`);
}

// ============================================================================
// ATTENDANCE API
// ============================================================================

export async function getAttendance(filters?: AttendanceFilters): Promise<{ attendance: Attendance[] }> {
  const params = new URLSearchParams();
  if (filters?.employeeId) params.append('employeeId', filters.employeeId);
  if (filters?.startDate) params.append('startDate', filters.startDate);
  if (filters?.endDate) params.append('endDate', filters.endDate);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.month) params.append('month', filters.month);

  const query = params.toString();
  return get<{ attendance: Attendance[] }>(`/salary/attendance${query ? `?${query}` : ''}`);
}

export async function getAttendanceByEmployee(
  employeeId: string,
  filters?: AttendanceFilters
): Promise<{ attendance: Attendance[]; summary: AttendanceSummary }> {
  const params = new URLSearchParams();
  if (filters?.startDate) params.append('startDate', filters.startDate);
  if (filters?.endDate) params.append('endDate', filters.endDate);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.month) params.append('month', filters.month);

  const query = params.toString();
  return get<{ attendance: Attendance[]; summary: AttendanceSummary }>(
    `/salary/attendance/employee/${employeeId}${query ? `?${query}` : ''}`
  );
}

export async function getAttendanceByDate(date: string): Promise<{
  date: string;
  attendance: Attendance[];
  unmarkedEmployees: { employeeId: string; name: string }[];
  summary: { totalEmployees: number; marked: number; unmarked: number };
}> {
  return get(`/salary/attendance/date/${date}`);
}

export async function createAttendance(data: AttendanceCreateInput): Promise<{ attendance: Attendance }> {
  return post<{ attendance: Attendance }>('/salary/attendance', data);
}

export async function bulkCreateAttendance(
  data: AttendanceBulkInput
): Promise<{ attendance: Attendance[]; count: number }> {
  return post<{ attendance: Attendance[]; count: number }>('/salary/attendance/bulk', data);
}

/**
 * Create attendance records for multiple dates at once
 */
export async function bulkCreateMultiDateAttendance(data: {
  dates: string[];
  records: { employeeId: string; status: AttendanceStatus; notes?: string }[];
}): Promise<{
  message: string;
  results: {
    created: { date: string; employeeId: string; status: AttendanceStatus }[];
    skipped: { date: string; employeeId: string; reason: string }[];
  };
}> {
  return post('/salary/attendance/bulk-multi-date', data);
}

export async function updateAttendance(
  attendanceId: string,
  employeeId: string,
  data: Partial<Attendance>
): Promise<{ attendance: Attendance }> {
  return put<{ attendance: Attendance }>(
    `/salary/attendance/${attendanceId}?employeeId=${employeeId}`,
    data
  );
}

// ============================================================================
// SALARY SLIP API
// ============================================================================

export async function getSalarySlips(filters?: SalarySlipFilters): Promise<{
  salarySlips: SalarySlip[];
  summary: { count: number; totalNetPay: number; totalGross: number; totalDeductions: number };
}> {
  const params = new URLSearchParams();
  if (filters?.employeeId) params.append('employeeId', filters.employeeId);
  if (filters?.month) params.append('month', filters.month);
  if (filters?.year) params.append('year', filters.year.toString());
  if (filters?.status) params.append('status', filters.status);

  const query = params.toString();
  return get(`/salary/slips${query ? `?${query}` : ''}`);
}

/**
 * Get salary slips for the logged-in employee (no admin required)
 */
export async function getMySalarySlips(): Promise<{
  salarySlips: SalarySlip[];
  summary: { count: number; totalNetPay: number; totalGross: number; totalDeductions: number };
}> {
  return get('/salary/my-slips');
}

export async function getSalarySlipsByEmployee(employeeId: string): Promise<{
  employee: Employee;
  salarySlips: SalarySlip[];
}> {
  return get(`/salary/slips/employee/${employeeId}`);
}

export async function getSalarySlipById(
  salarySlipId: string,
  employeeId: string
): Promise<{ salarySlip: SalarySlip }> {
  return get<{ salarySlip: SalarySlip }>(
    `/salary/slips/${salarySlipId}?employeeId=${employeeId}`
  );
}

export async function generateSalarySlip(
  data: SalarySlipCreateInput
): Promise<{ salarySlip: SalarySlip }> {
  return post<{ salarySlip: SalarySlip }>('/salary/slips/generate', data);
}

export async function generateBulkSalarySlips(data: {
  month: string;
  year: number;
  employeeIds?: string[];
  totalWorkingDays?: number;
}): Promise<{
  message: string;
  results: { success: string[]; failed: { employeeId: string; error: string }[] };
}> {
  return post('/salary/slips/generate-bulk', data);
}

export async function updateSalarySlip(
  salarySlipId: string,
  employeeId: string,
  data: Partial<SalarySlip>
): Promise<{ salarySlip: SalarySlip }> {
  return put<{ salarySlip: SalarySlip }>(
    `/salary/slips/${salarySlipId}?employeeId=${employeeId}`,
    data
  );
}

export async function markSalarySlipStatus(
  salarySlipId: string,
  employeeId: string,
  status: SalarySlipStatus
): Promise<{ salarySlip: SalarySlip }> {
  return post<{ salarySlip: SalarySlip }>(
    `/salary/slips/${salarySlipId}/mark-status`,
    { employeeId, status }
  );
}

export async function deleteSalarySlip(
  salarySlipId: string,
  employeeId: string
): Promise<{ message: string }> {
  return del<{ message: string }>(
    `/salary/slips/${salarySlipId}?employeeId=${employeeId}`
  );
}

// ============================================================================
// EMAIL API
// ============================================================================

export async function sendSalarySlipEmail(
  salarySlipId: string,
  employeeId: string
): Promise<{ message: string; sentTo: string }> {
  return post<{ message: string; sentTo: string }>(
    `/salary/slips/${salarySlipId}/send-email`,
    { employeeId }
  );
}

export async function sendBulkSalarySlipEmails(
  slips: { salarySlipId: string; employeeId: string }[]
): Promise<{
  message: string;
  results: { success: number; failed: number; errors: { employeeId: string; error: string }[] };
}> {
  return post('/salary/slips/send-bulk-emails', { salarySlipIds: slips });
}

// ============================================================================
// DASHBOARD API
// ============================================================================

export async function getPayrollDashboard(): Promise<{ summary: PayrollDashboard }> {
  return get<{ summary: PayrollDashboard }>('/salary/dashboard');
}

// ============================================================================
// HR REVAMP API — pending summary, recent activity, approve, mark-paid
// ============================================================================

export interface HrPendingSummary {
  today: string;
  currentMonth: string;
  currentYear: number;
  unmarkedAttendanceToday: number;
  markedAttendanceToday: number;
  draftSlipsThisMonth: number;
  generatedSlipsThisMonth: number;
  pendingApprovalSlipsThisMonth: number;
  sentSlipsThisMonth: number;
  paidSlipsThisMonth: number;
  headcount: number;
  joinersThisMonth: number;
  latestJoinerName: string | null;
}

export interface HrActivityItem {
  type: string;
  message: string;
  timestamp: string;
}

/**
 * HR Home dashboard data — synthesised from existing endpoints until the
 * dedicated /pending-summary and /recent-activity endpoints are deployed.
 * Avoids 404 noise in the network tab on every dashboard load.
 */
export async function getHrPendingSummary(): Promise<{ summary: HrPendingSummary }> {
  if (USE_NEW_ENDPOINTS) {
    return get<{ summary: HrPendingSummary }>('/salary/pending-summary');
  }
  const today = new Date();
  const todayISO = today.toISOString().slice(0, 10);
  const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const currentYear = today.getFullYear();

  const [empRes, slipRes] = await Promise.all([
    getEmployees(true),
    getSalarySlips({ month: currentMonth, year: currentYear }),
  ]);

  // Attendance for today — needed for marked/unmarked counters
  let markedAttendanceToday = 0;
  try {
    const att = await getAttendanceByDate(todayISO);
    markedAttendanceToday = att.summary.marked;
  } catch { /* if endpoint hiccups, leave counters at 0 */ }

  const slipsThisMonth = slipRes.salarySlips;
  const count = (s: SalarySlipStatus) => slipsThisMonth.filter((x) => x.status === s).length;

  const monthStart = `${currentYear}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  const joiners = empRes.employees
    .filter((e) => e.joiningDate && e.joiningDate.slice(0, 10) >= monthStart && e.joiningDate.slice(0, 10) <= todayISO)
    .sort((a, b) => (b.joiningDate || '').localeCompare(a.joiningDate || ''));

  return {
    summary: {
      today: todayISO,
      currentMonth,
      currentYear,
      unmarkedAttendanceToday: Math.max(0, empRes.employees.length - markedAttendanceToday),
      markedAttendanceToday,
      draftSlipsThisMonth: count(SalarySlipStatus.DRAFT),
      generatedSlipsThisMonth: count(SalarySlipStatus.GENERATED),
      pendingApprovalSlipsThisMonth: count(SalarySlipStatus.PENDING_APPROVAL),
      sentSlipsThisMonth: count(SalarySlipStatus.SENT),
      paidSlipsThisMonth: count(SalarySlipStatus.PAID),
      headcount: empRes.employees.length,
      joinersThisMonth: joiners.length,
      latestJoinerName: joiners[0]?.name ?? null,
    },
  };
}

export async function getHrRecentActivity(limit = 10): Promise<{ activity: HrActivityItem[] }> {
  if (USE_NEW_ENDPOINTS) {
    return get<{ activity: HrActivityItem[] }>(`/salary/recent-activity?limit=${limit}`);
  }
  // Synthesise activity feed from current data — newest first
  const [empRes, slipRes] = await Promise.all([
    getEmployees(false),
    getSalarySlips(),
  ]);
  const items: HrActivityItem[] = [];

  // Recent joiners (60 days)
  const sixtyDaysAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString();
  for (const e of empRes.employees) {
    if ((e.createdAt || '') >= sixtyDaysAgo) {
      items.push({
        type: 'employee_joined',
        message: `${e.name} joined as ${e.designation || 'employee'}`,
        timestamp: e.createdAt,
      });
    }
  }

  // Slip rollups by month
  type Bucket = { generated: number; sent: number; paid: number; latest: string };
  const buckets = new Map<string, Bucket>();
  for (const s of slipRes.salarySlips) {
    if ((s.updatedAt || '') < sixtyDaysAgo) continue;
    const key = s.month;
    const b = buckets.get(key) || { generated: 0, sent: 0, paid: 0, latest: s.updatedAt };
    if (s.status === SalarySlipStatus.GENERATED || s.status === SalarySlipStatus.PENDING_APPROVAL || s.status === SalarySlipStatus.DRAFT) b.generated += 1;
    if (s.status === SalarySlipStatus.SENT) b.sent += 1;
    if (s.status === SalarySlipStatus.PAID) b.paid += 1;
    if ((s.updatedAt || '') > b.latest) b.latest = s.updatedAt;
    buckets.set(key, b);
  }
  for (const [month, b] of buckets.entries()) {
    if (b.paid > 0) items.push({ type: 'slips_paid', message: `${month} payroll: ${b.paid} slips marked paid`, timestamp: b.latest });
    else if (b.sent > 0) items.push({ type: 'slips_sent', message: `${month} payroll: ${b.sent} slips sent`, timestamp: b.latest });
    else if (b.generated > 0) items.push({ type: 'slips_generated', message: `${month} payroll: ${b.generated} slips generated`, timestamp: b.latest });
  }

  items.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
  return { activity: items.slice(0, limit) };
}

/**
 * Whether the new HR-revamp endpoints (/approve, /mark-paid, /mark-batch-paid,
 * /pending-summary, etc.) have been deployed to the dev backend.
 *
 * Backend was deployed on 2026-06-11 so this is now `true`. The legacy
 * fallback paths in this file remain useful as a safety net if backend ever
 * gets rolled back — set this to `false` and everything still works.
 */
const USE_NEW_ENDPOINTS = true;

/**
 * "Approve & Send" — calls the legacy endpoints (send-email then mark-status)
 * by default. Once backend is deployed, flip USE_NEW_ENDPOINTS to use the
 * single-call /approve endpoint that also writes the approval audit trail.
 */
export async function approveSlip(
  salarySlipId: string,
  employeeId: string
): Promise<{ salarySlip: SalarySlip; sentTo: string }> {
  if (USE_NEW_ENDPOINTS) {
    return post<{ salarySlip: SalarySlip; sentTo: string }>(
      `/salary/slips/${salarySlipId}/approve`,
      { employeeId }
    );
  }
  const emailRes = await sendSalarySlipEmail(salarySlipId, employeeId);
  const statusRes = await markSalarySlipStatus(salarySlipId, employeeId, SalarySlipStatus.SENT);
  return { salarySlip: statusRes.salarySlip, sentTo: emailRes.sentTo };
}

/**
 * Mark a slip as PAID. Uses legacy mark-status until backend deploys.
 */
export async function markSlipPaid(
  salarySlipId: string,
  employeeId: string
): Promise<{ salarySlip: SalarySlip }> {
  if (USE_NEW_ENDPOINTS) {
    return post<{ salarySlip: SalarySlip }>(
      `/salary/slips/${salarySlipId}/mark-paid`,
      { employeeId }
    );
  }
  return markSalarySlipStatus(salarySlipId, employeeId, SalarySlipStatus.PAID);
}

export async function markBatchPaid(
  month: string,
  year: number
): Promise<{ marked: number; total: number }> {
  if (USE_NEW_ENDPOINTS) {
    return post<{ marked: number; total: number }>('/salary/slips/mark-batch-paid', { month, year });
  }
  // Legacy path: fetch every SENT slip for the month and mark-status each
  const { salarySlips } = await getSalarySlips({ month, year, status: SalarySlipStatus.SENT });
  let marked = 0;
  for (const s of salarySlips) {
    try {
      await markSalarySlipStatus(s.salarySlipId, s.employeeId, SalarySlipStatus.PAID);
      marked += 1;
    } catch { /* skip — best effort */ }
  }
  return { marked, total: salarySlips.length };
}

// ============================================================================
// EXPORT SERVICE OBJECT
// ============================================================================

export const salaryService = {
  // Employees
  getEmployees,
  getEmployeeById,
  createEmployee,
  updateEmployee,
  deleteEmployee,

  // Attendance
  getAttendance,
  getAttendanceByEmployee,
  getAttendanceByDate,
  createAttendance,
  bulkCreateAttendance,
  bulkCreateMultiDateAttendance,
  updateAttendance,

  // Salary Slips
  getSalarySlips,
  getMySalarySlips,
  getSalarySlipsByEmployee,
  getSalarySlipById,
  generateSalarySlip,
  generateBulkSalarySlips,
  updateSalarySlip,
  markSalarySlipStatus,
  deleteSalarySlip,

  // Email
  sendSalarySlipEmail,
  sendBulkSalarySlipEmails,

  // Dashboard
  getPayrollDashboard,

  // HR revamp
  getHrPendingSummary,
  getHrRecentActivity,
  approveSlip,
  markSlipPaid,
  markBatchPaid,
};

export default salaryService;
